import { useCallback, useMemo } from "react"
import { Stack } from "@mui/material"
import AddFilterNew from "components/add-filter"
import NormalizationRadioGroup from "components/item-radio-group"
import FilterScopeRadioGroup, { CONTRIBUTION_DISABLED_BY_FILTER_SCOPE_CAPTION } from "components/filter-scope-radio-group"
import { NORMALIZATION } from "helpers/segments"
import {
  FILTER_SCOPE,
  DEFAULT_FILTER_SCOPE,
  parseFilterScope,
  computeEffectiveFilterScope,
  computeEffectiveNormalization,
  computeHasTagOrAttributeFilter,
  type FilterScope,
} from "helpers/filter-scope"
import type { FilterOn } from "helpers/filters/filter-ons"
import { usePendingPick } from "./use-pending-pick"
import { computePendingFilterOns } from "./pending-filter-ons"

const TAG_OR_ATTRIBUTE_CONTRIBUTION_TOOLTIP = "Filtering on position tags or attributes is not allowed in contribution mode when active."
const PORTFOLIO_ONLY_FILTER_NOTE = "Filters apply to your portfolio only; the benchmark is unfiltered."

function getContributionDisabledReason(isPortfolioOnlyPending: boolean, hasTagOrAttributeFilter: boolean): { tooltip?: string, caption?: string } {
  if (hasTagOrAttributeFilter) {
    return { tooltip: TAG_OR_ATTRIBUTE_CONTRIBUTION_TOOLTIP }
  }
  return isPortfolioOnlyPending ? { caption: CONTRIBUTION_DISABLED_BY_FILTER_SCOPE_CAPTION } : {}
}

function getDisplayedFilterScope(isFilterScopeUnavailable: boolean, isLockedByFilter: boolean, pendingFilterScope: FilterScope): FilterScope {
  if (isFilterScopeUnavailable) {
    return DEFAULT_FILTER_SCOPE
  }
  return isLockedByFilter ? FILTER_SCOPE.PORTFOLIO_ONLY : pendingFilterScope
}

interface AnalyzeHeaderFiltersProps {
  normalization?: string
  setNormalization: (normalization: string) => void
  filterOns: FilterOn[]
  account: string
  modelId?: string | null
  assetId?: string
  assetType?: string
  startDate?: string
  endDate?: string
  heapAddFilterId: string
  disableNormalization?: boolean
  disableNormalizationTooltip?: string | null
  forcedNormalization?: string
  showFilterScope?: boolean
  filterScope?: string
  setFilterScope?: (filterScope: FilterScope) => void
  hasBenchmark?: boolean
  baseType?: string
  filter?: unknown
  isFilterScopeUnavailable?: boolean
  intraday?: boolean
  isExpanded?: boolean
  hasUnsupportedContributionFilter?: boolean
  currency?: string
  securityIds?: unknown
  filtersVisible?: boolean
  toggleFilters?: (...args: unknown[]) => void
  anchorElement?: HTMLElement | null
  addedFilters?: unknown[]
  updateFilters: (filters: unknown) => void
}

export default function AnalyzeHeaderFilters({
  normalization,
  setNormalization,
  filterOns,
  account,
  modelId,
  assetId,
  assetType,
  startDate,
  endDate,
  heapAddFilterId,
  disableNormalization,
  disableNormalizationTooltip,
  forcedNormalization,
  showFilterScope = false,
  filterScope,
  setFilterScope,
  hasBenchmark = false,
  baseType,
  filter,
  isFilterScopeUnavailable = false,
  intraday,
  isExpanded,
  hasUnsupportedContributionFilter,
  currency,
  securityIds,
  filtersVisible,
  toggleFilters,
  anchorElement,
  addedFilters,
  updateFilters,
}: AnalyzeHeaderFiltersProps) {
  const { value: internalNormalization, setPick: setInternalNormalization, clearPick: clearNormalizationPick } = usePendingPick(normalization)
  const { value: internalFilterScope, setPick: setInternalFilterScope, clearPick: clearFilterScopePick } = usePendingPick(parseFilterScope(filterScope))

  const handleUpdateFilters = useCallback((filters: unknown) => {
    updateFilters(filters)
    if (internalNormalization) {
      setNormalization(internalNormalization)
    }
    if (showFilterScope && setFilterScope && !isFilterScopeUnavailable) {
      setFilterScope(internalFilterScope)
    }
  }, [updateFilters, setNormalization, internalNormalization, showFilterScope, setFilterScope, isFilterScopeUnavailable, internalFilterScope])

  const handleSetFilterScope = useCallback((value: string) => setInternalFilterScope(parseFilterScope(value)), [setInternalFilterScope])

  const handleToggleFilters = useCallback((...args: unknown[]) => {
    clearNormalizationPick()
    clearFilterScopePick()
    toggleFilters?.(...args)
  }, [clearNormalizationPick, clearFilterScopePick, toggleFilters])

  const isFilterScopeControlAvailable = showFilterScope && !isFilterScopeUnavailable
  const isLockedByFilter = isFilterScopeControlAvailable && hasBenchmark && computeHasTagOrAttributeFilter(filter)
  const pendingEffectiveFilterScope = showFilterScope
    ? computeEffectiveFilterScope(internalFilterScope, { hasBenchmark, hasSimulatedTrades: isFilterScopeUnavailable, hasTagOrAttributeFilter: isLockedByFilter })
    : DEFAULT_FILTER_SCOPE
  const isPortfolioOnlyPending = pendingEffectiveFilterScope === FILTER_SCOPE.PORTFOLIO_ONLY
  const displayedNormalization = forcedNormalization ?? computeEffectiveNormalization(internalNormalization, pendingEffectiveFilterScope)
  const isStandalonePending = displayedNormalization === NORMALIZATION.STANDALONE
  const shouldRederiveFilterOns = isFilterScopeControlAvailable && hasBenchmark
  const hasTagOrAttributeFilter = Boolean(hasUnsupportedContributionFilter) || isLockedByFilter

  const pendingFilterOns = useMemo(
    () => shouldRederiveFilterOns ? computePendingFilterOns(filterOns, isStandalonePending) : filterOns,
    [shouldRederiveFilterOns, filterOns, isStandalonePending],
  )

  const contributionDisabledReason = getContributionDisabledReason(isPortfolioOnlyPending, hasTagOrAttributeFilter)

  const normalizationOptions = [
    {
      id: NORMALIZATION.CONTRIBUTION,
      primaryText: "Contribution",
      secondaryText: <>Constituents&apos; weights are calculated by their respective contribution to the non-filtered portfolio. <i>Default</i></>,
      disabled: hasTagOrAttributeFilter || isPortfolioOnlyPending,
      tooltip: contributionDisabledReason.tooltip ?? null,
    },
    {
      id: NORMALIZATION.STANDALONE,
      primaryText: "Standalone",
      secondaryText: "The weights of the segment constituents are calculated as if it were a standalone portfolio",
    },
  ]

  const normalizationGroup = (
    <NormalizationRadioGroup
      disabled={disableNormalization}
      disableItemTooltip={true}
      disabledTooltip={disableNormalizationTooltip}
      label="Normalization"
      value={displayedNormalization}
      setValue={setInternalNormalization}
      options={normalizationOptions}
      helperText={contributionDisabledReason.caption}
      classes={{ label: "filter-header" }}
    />
  )

  const additionalFilterComponent = showFilterScope ? (
    <Stack spacing={1}>
      {normalizationGroup}
      <FilterScopeRadioGroup
        value={getDisplayedFilterScope(isFilterScopeUnavailable, isLockedByFilter, internalFilterScope)}
        setValue={handleSetFilterScope}
        hasBenchmark={hasBenchmark}
        isUnavailable={isFilterScopeUnavailable}
        isLockedByFilter={isLockedByFilter}
        baseType={baseType}
      />
    </Stack>
  ) : normalizationGroup

  return (
    <AddFilterNew
      filterOns={pendingFilterOns}
      updateFilters={handleUpdateFilters}
      account={account}
      modelId={modelId}
      assetId={assetId}
      assetType={assetType}
      startDate={startDate}
      endDate={endDate}
      heapAddFilterId={heapAddFilterId}
      intraday={intraday}
      isExpanded={isExpanded}
      currency={currency}
      securityIds={securityIds}
      anchorEl={anchorElement}
      filtersVisible={filtersVisible}
      toggleFilters={handleToggleFilters}
      existingFilters={addedFilters ?? []}
      additionalFilterComponent={additionalFilterComponent}
      filterNote={isPortfolioOnlyPending ? PORTFOLIO_ONLY_FILTER_NOTE : undefined}
      showAdditionalFilterComponentOnActiveFilterOnly={true}
      isApplyButtonDisabledFunction={undefined}
    />
  )
}
